import itemsStorage from '../../storages/items-storage';
import styles from './parts-list.module.scss';
import { Button, Card, Col, Image, Input, InputRef, Row, Select, Space, Table, Tag, TreeSelect } from "antd";
import { observer } from "mobx-react-lite";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import brandsStorage from "../../storages/brands";
import partTypesStorage from "../../storages/part-types";
import AddIcon from '@ant-design/icons/PlusOutlined';
import ViewIcon from '@ant-design/icons/EyeOutlined';
import layoutStorage from '../../storages/layout-storage';
import { IBrand, IItem, IVariant } from "@drone-parts.garphild.pro/interfaces";
import ExpandedVariants from "../../components/expanded-variants";
import type { ColumnType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { normalizeUrl } from "../../helpers/normalize-url";
import xor from 'lodash/xor';
import classNames from 'classnames';
import PartTypeSelector from "../../components/part-type-selector";

/* eslint-disable-next-line */
export interface PartsListProps {
  filters?: {
    brandId?: string[]
    partTypeId?: string[]
  }
}

export function PartsList(props: PartsListProps) {
  const { loadItems, items } = itemsStorage;
  const { toggleAddItemDialog } = layoutStorage;
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);
  const [brandsFilter, setBrandsFilter] = useState<IBrand['_id'][]>([]);
  const [partTypesFilter, setPartTypesFilter] = useState<string[]>([]);
  const [searchBrandFilter, setSearchBrandFilter] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  const { partTypes } = partTypesStorage;

  useEffect(() => {
    setBrandsFilter(props.filters?.brandId || []);
    setPartTypesFilter(props.filters?.partTypeId || []);
  }, [props.filters?.brandId, props.filters?.partTypeId]);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: any,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const handleChangeBrandFilter = (value: IBrand["_id"]) => {
    setBrandsFilter(brandsFilter.filter((id) => id !== value));
  }

  const handleChangePartTypeFilter = (value: string | string[]) => {
    if (!Array.isArray(value)) {
      value = [value];
    }

    setPartTypesFilter(xor(partTypesFilter, value));
  }

  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const getColumnSearchProps = (dataIndex: string): ColumnType<IItem> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: any) => {
      if (['brandId', 'partTypeId'].includes(dataIndex)) {
        let options;
        switch (dataIndex) {
          case 'brandId':
            options = brandsStorage.brands.map((brand) => ({ value: brand._id?.toString() || 'n/a', label: brand.name }));
            break;
          case 'partTypeId':
            options = partTypesStorage.partTypes.map((partType) => ({ value: partType?.key || 'n/a', label: partType?.name }));
            break;
        }
        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Space direction="vertical">
              <Select
                size="small"
                showSearch
                filterOption={(input: string, option?: { label: string; value: string }) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                mode="multiple"
                defaultValue={selectedKeys}
                tagRender={(props): ReactElement => {
                  return <Tag
                    // color={props.value}
                    onMouseDown={onPreventMouseDown}
                    closable={props.closable}
                    onClose={props.onClose}
                    style={{ marginRight: 3 }}
                  >
                    {props.label || props.value}
                  </Tag>;
                }}
                value={selectedKeys}
                onChange={setSelectedKeys}
                style={{ width: '100%', display: 'block' }}
                options={options}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                  icon={<SearchOutlined/>}
                  size="small"
                  style={{ width: 90 }}
                >
                  Search
                </Button>
                <Button
                  onClick={() => clearFilters && handleReset(clearFilters)}
                  size="small"
                  style={{ width: 90 }}
                >
                  Reset
                </Button>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    confirm({ closeDropdown: false });
                    setSearchText((selectedKeys as string[])[0]);
                    setSearchedColumn(dataIndex);
                  }}
                >
                  Filter
                </Button>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    close();
                  }}
                >
                  close
                </Button>
              </Space>
            </Space>
          </div>
        );
      }
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
              icon={<SearchOutlined/>}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                confirm({ closeDropdown: false });
                setSearchText((selectedKeys as string[])[0]);
                setSearchedColumn(dataIndex);
              }}
            >
              Filter
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                close();
              }}
            >
              close
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record): boolean => {
      const searchString = normalizeUrl(value as string).toLowerCase();
      if (!record) return false;

      switch (dataIndex) {
        case 'officialUrl':
          if (record?.[dataIndex]?.toLowerCase().includes(searchString)) return true;
          if (record?.variants?.some((variant: IVariant) => variant.url.toLowerCase().includes(searchString))) return true;
          return false;
        case 'partTypeId':
          return record.partTypeId.includes(value as string);
        case 'brandId':
          return +record.brandId === +(value as string);
        default:
          return !!record?.[dataIndex as keyof IItem]
            ?.toString()
            ?.toLowerCase()
            ?.includes((value as string).toLowerCase()) || false;
      }
    },
    // onFilterDropdownOpenChange: (visible) => {
    //   if (visible) {
    //     setTimeout(() => searchInput.current?.select(), 100);
    //   }
    // },
    // render: (text) =>
    //   searchedColumn === dataIndex ? (
    //     <Highlighter
    //       highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
    //       searchWords={[searchText]}
    //       autoEscape
    //       textToHighlight={text ? text.toString() : ''}
    //     />
    //   ) : (
    //     text
    //   ),
  });

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpandedRows = (id: string) => {
    if (expandedRows[id]) {
      setExpandedRows({
        ...expandedRows,
        [id]: false,
      });
    } else {
      setExpandedRows({
        ...expandedRows,
        [id]: true,
      });
    }
  }
  return (
    <div className={styles['container']}>
      <div>{JSON.stringify(partTypesFilter)}</div>
      <Row gutter={8} className={styles['filterBlock']}>
        <Col>
          <Input.Search
            className={styles['search']}
            onSearch={(value) => setSearchBrandFilter(value)}
            onInput={(e) => setSearchBrandFilter(e.currentTarget.value)}
          />
          {brandsFilter.map((brandId) => (
            <Tag closeIcon onClose={() => {
              const newBrandsFilter = brandsFilter.filter((id) => id !== brandId);
              setBrandsFilter(newBrandsFilter);
            }}>
              {brandsStorage.brands?.find((brand) => brand._id === brandId)?.name}
            </Tag>
          ))}
        </Col>
        {brandsStorage
          .brands
          .filter((brand) => searchBrandFilter.length > 0 ? brand.name.toLowerCase().includes(searchBrandFilter.toLowerCase()) : true)
          .map((brand) => {
          return (
            <Col key={brand._id} style={{ marginBottom: 8}} onClick={() => handleChangeBrandFilter(brand._id)}>
              <Card
                className={classNames({
                  [styles['selected']]: brandsFilter.includes(brand._id),
                })}
                style={{ width: 150, height: '100%', marginBottom: 8, display: "flex", alignContent: 'space-between' }}
              >
                <Image src={brand.logoUrl} width='100%' preview={false} />
                <div>items: {brand.itemsCount}</div>
              </Card>
            </Col>
          );
        })}
      </Row>
      <Row gutter={8} className={styles['filterBlock']}>
        {partTypesStorage.partTypes.map((partType) => {
          return (
            <Col
              key={partType._id}
              style={{ marginBottom: 8}}
              onClick={() => handleChangePartTypeFilter(partType._id)}
            >
              <Card
                className={classNames({
                  [styles['selected']]: partTypesFilter.includes(partType._id),
                })}
                style={{ width: 150, height: '100%', marginBottom: 8, display: "flex", alignContent: 'space-between' }}
              >
                {/*<Image src={partType.logoUrl} width='100%' preview={false} />*/}
                {/*<div>items: {partType.itemsCount}</div>*/}
                <div>{partType.name}</div>
              </Card>
            </Col>
          );
        })}
      </Row>
      <Table
        expandable={{
          onExpand: (expanded, record) => {
            toggleExpandedRows(record._id);
          },
          // expandedRowKeys: Object.keys(expandedRows).filter((key) => !!expandedRows[key]),
          onExpandedRowsChange: (expandedRows) => {
            console.log(expandedRows);
          },
          expandedRowRender: (record: IItem) => <ExpandedVariants variants={record.variants || []} />,
          rowExpandable: (record) => (record.variants?.length || 0) > 0,
        }}
        columns={[
          {
            title: "ID",
            dataIndex: "_id",
            key: "_id",
          },
          {
            title: "Brand",
            dataIndex: "brandId",
            key: "brandId",
            render: (brandId: IBrand['_id']) => {
              const brand = brandsStorage.brands?.find((brand) => brand._id === brandId);
              return (
                <Row justify="start" align="middle">
                  {brand?.logoUrl && <Col><Image preview={false} className="logo-in-table" src={`/${brand?.logoUrl}`} alt="logo" /></Col>}
                  <Col>{brand?.name}</Col>
                </Row>
              );
            },
            ...getColumnSearchProps('brandId'),
          },
          {
            title: "Type",
            dataIndex: "partTypeId",
            key: "partTypeId",
            render: (partTypeId: string[], record: IItem) => {
              return (
                <>
                  <PartTypeSelector
                    partType={record.partTypeId}
                    onChange={(value) => itemsStorage.handleChangePartType(record._id, value)}
                  />
                <TreeSelect
                  style={{ width: '100%', minWidth: 250 }}
                  placeholder="Select/enter category"
                  showSearch
                  value={record.partTypeId}
                  tagRender={(props) => {
                    const [category] = props.value?.split(':') || ['',''];
                    const partType = partTypes.find((partType) => partType.key === category);
                    const subPartType = partType?.subcategories?.find((subPartType) => subPartType.key === props.value);
                    return <Tag
                      closable={props.closable}
                      onClose={props.onClose}
                      style={{ marginRight: 3 }}
                    >
                      {partType?.name}
                      {'->'}
                      {subPartType?.name}
                    </Tag>;
                  }}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  treeDefaultExpandAll
                  multiple
                  treeCheckable
                  popupClassName={'g-select'}
                  treeData={partTypes.map((partType) => ({
                    title: partType.name,
                    value: partType.key,
                    children: partType.subcategories?.map((subPartType) => ({
                      title: subPartType.name,
                      value: subPartType.key,
                    })),
                  }))}
                  onChange={(value: string[]) => {
                    itemsStorage.handleChangePartType(record._id, value);
                    console.log('selected on change', value);
                  }}
                  onSelect={(value) => {
                    console.log('selected', value);
                  }}
                />
                </>
              )
              // const { partTypesFull } = partTypesStorage;
              // const partType = partTypesFull.find((partType) => partType.key === partTypeId);
              // return <Select
              //   showSearch={true}
              //   value={partTypeId}
              //   style={{ width: '100%', minWidth: 250}}
              //   options={partTypesFull.map((partType) => ({ label: partType.name, value: partType.key }))}
              //   onChange={(value) => itemsStorage.handleChangePartType(record._id, value)}
              // />;
            },
            ...getColumnSearchProps('partTypeId'),
          },
          {
            title: "Name",
            dataIndex: "name",
            key: "name",
            ...getColumnSearchProps('name'),
          },
          {
            title: "prices",
            dataIndex: "priceMin",
            key: "prices",
            render: (priceMin: number, record) => {
              return (
                <>
                  <div>official: {(record?.officialPrice || 0) / 100}</div>
                  <div>{priceMin && `min: ${priceMin / 100 || 0}`}{priceMin && (record?.priceMax || 0) / 100 && ' - '}{record.priceMax && `max: ${record.priceMax / 100 || 0}`}</div>
                </>
              );
            }
          },
          {
            title: "Official url",
            dataIndex: "officialUrl",
            key: "officialUrl",
            render: (officialUrl: string) => <a href={officialUrl} target="_blank" rel="noreferrer">Link</a>,
            ...getColumnSearchProps('officialUrl'),
          },
          {
            title: 'Actions',
            key: 'id',
            dataIndex: 'id',
            render: (id: string) => (
              <Row justify="end" align="middle" gutter={4}>
                <Col>
                  <Button type="primary" size="small" onClick={() => toggleAddItemDialog(id)}>
                    <AddIcon />
                  </Button>
                </Col>
                <Col>
                  <Button size="small" type="primary">
                    <ViewIcon />
                  </Button>
                </Col>
              </Row>
            )
          }
        ]}
        dataSource={items.filter((item) => {
          // if (item.partTypeId.length === 0) {
            return true;
          // } else {
          //   return false
          // }
          // const isBrandPass = brandsFilter.length > 0 ? brandsFilter.includes(item.brandId) : true;
          // const isPartTypePass = partTypesFilter.length > 0 ? intersection(partTypesFilter,item.partTypeId).length : true;
          // return isBrandPass && isPartTypePass;
        })}
        onRow={(record, rowIndex) => {
          return {
            onDoubleClick: event => {
              console.log(record, rowIndex, event);
              // onSelect(record.id);
            }
          };
        }}
      />
    </div>
  );
}

export default observer(PartsList);
